import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GradeBadge, AccessBadge, VerifiedTag } from "@/components/n360/Badges";
import { ROLE_LABEL } from "@/lib/content";

const cell = "px-3 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#0D3C4C] text-xs focus:outline-none focus:border-[#F1551D]/60";

export default function AdminPanel() {
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/members").then(({ data }) => setMembers(data.members));
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    try {
      await api.put(`/admin/members/${id}`, patch);
      toast.success("Member updated.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const filtered = members.filter((m) => !q || `${m.name} ${m.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="rise">
      <div className="mb-6">
        <div className="font-mono-i text-[11px] tracking-[0.3em] text-[#F1551D]/90 uppercase mb-2">Sovereign Oversight</div>
        <h1 className="font-display text-3xl text-[#0D3C4C]">Admin — Member Governance</h1>
        <p className="text-[#5f7d86] mt-1 text-sm">Assign grading tiers, clearance levels, roles, and verification status.</p>
      </div>

      <div className="n360-card p-4 mb-5">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" data-testid="admin-search" className="w-full px-4 py-2 rounded-lg bg-[#F6EEDC] border border-[#F1551D]/20 text-[#0D3C4C] placeholder:text-[#aab9bd] focus:outline-none focus:border-[#F1551D]/60 text-sm" />
      </div>

      <div className="n360-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#8598a0] border-b border-[#F1551D]/15">
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Grade</th>
              <th className="px-4 py-3">Access</th>
              <th className="px-4 py-3">Verified</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} className="border-b border-[#F1551D]/8 hover:bg-white/[0.02]" data-testid={`admin-row-${m.id}`}>
                <td className="px-4 py-3">
                  <div className="text-[#0D3C4C]">{m.name} <VerifiedTag verified={m.verified} /></div>
                  <div className="text-[11px] text-[#8598a0]">{m.email}</div>
                </td>
                <td className="px-4 py-3">
                  <select value={m.role} onChange={(e) => update(m.id, { role: e.target.value })} className={cell} data-testid={`admin-role-${m.id}`}>
                    {(meta?.roles || []).map((r) => <option key={r} value={r} className="bg-[#F6EEDC]">{ROLE_LABEL[r] || r}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={m.grade} onChange={(e) => update(m.id, { grade: e.target.value })} className={cell} data-testid={`admin-grade-${m.id}`}>
                    {(meta?.grades || []).map((g) => <option key={g} value={g} className="bg-[#F6EEDC]">{g}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={m.access_level} onChange={(e) => update(m.id, { access_level: e.target.value })} className={cell} data-testid={`admin-access-${m.id}`}>
                    {(meta?.access_levels || []).map((a) => <option key={a} value={a} className="bg-[#F6EEDC]">{a}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => update(m.id, { verified: !m.verified })} data-testid={`admin-verify-${m.id}`} className={`px-3 py-1 rounded-full text-xs border ${m.verified ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-slate-600 text-[#5f7d86]"}`}>
                    {m.verified ? "Verified" : "Verify"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  {m.profile_id && <button onClick={() => navigate(`/app/member/${m.profile_id}`)} className="text-[#F1551D] text-xs hover:underline">View</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
